import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Eye, EyeOff, Globe, LogIn } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useLang } from '@/providers/LangProvider';
import { useToast } from '@/providers/ToastProvider';
import { clinicName } from '@/lib/supabase';
import { resolveStaffAuthEmail } from '@/lib/staffLoginIdentifier';
import { errorMessage } from '@/lib/errorMessage';
import { t } from '@/i18n/labels';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';

export function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const { lang, setLang, dir } = useLang();
  const toast = useToast();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(resolveStaffAuthEmail(login), password);
      toast.success(t(lang, 'welcomeBack'));
    } catch (err) {
      toast.error(t(lang, 'signIn'), errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page" dir={dir}>
      <div className="auth-locale-row" style={{ position: 'absolute', insetBlockStart: 'calc(1rem + var(--safe-top))', insetInlineEnd: 'calc(1rem + var(--safe-right))', padding: 0 }}>
        <IconButton aria-label={t(lang, 'language')} variant="ghost" size="sm" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
          <Globe size={16} />
        </IconButton>
      </div>

      <div className="auth-card">
        <div className="brand-mark" aria-hidden />
        <div>
          <h1>{clinicName}</h1>
          <p className="auth-sub">{t(lang, 'appTitle')}</p>
        </div>

        <form className="auth-form" onSubmit={onSubmit} noValidate>
          <label className="field">
            <span>{t(lang, 'emailOrUsername')}</span>
            <input
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              required
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder={t(lang, 'emailOrUsernamePlaceholder')}
            />
            <span className="field-hint">{t(lang, 'loginHint')}</span>
          </label>

          <label className="field">
            <span>{t(lang, 'password')}</span>
            <div className="password-row" style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ flex: 1, paddingInlineEnd: '44px' }}
              />
              <button
                type="button"
                aria-label={showPassword ? t(lang, 'hidePassword') : t(lang, 'showPassword')}
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: 'absolute',
                  insetBlock: 0,
                  insetInlineEnd: 6,
                  margin: 'auto 0',
                  width: 34,
                  height: 34,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 10,
                  border: 0,
                  background: 'transparent',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          <Button
            variant="primary"
            type="submit"
            size="lg"
            block
            loading={busy}
            leadingIcon={busy ? undefined : <LogIn size={16} />}
          >
            {busy ? t(lang, 'loading') : t(lang, 'signIn')}
          </Button>
        </form>
      </div>
    </div>
  );
}
